import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { z } from 'zod';
import ProgressButton from '@/components/common/progress-button';
import { FieldMessage } from '@/components/fields/field-message';
import PasswordField from '@/components/fields/password-field';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import useVerifyPassword from '@/hooks/auth/use-verify-password';
import useIsPasswordSet from '@/hooks/settings/use-is-password-set';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import useUserPlatform from '@/hooks/use-user-platform';
import useUserInfo from '@/hooks/users/use-user-info';
import ROUTES from '@/routes';

export default function SecuritySettingsIndexRoute() {
  const { isDesktop } = useUserPlatform();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: userInfo } = useUserInfo();
  const { mutateAsync: verifyPassword, isPending } = useVerifyPassword();
  const { data: isPasswordSet } = useIsPasswordSet();

  useAdjustLayout();

  const {
    formState: { errors, isDirty },
    handleSubmit,
    register,
  } = useForm<UpdatePasswordVerificationData>({
    resolver: zodResolver(updatePasswordVerificationSchema),
    defaultValues: {
      password: '',
    },
  });

  const handleVerifyOtpCode = async (data: UpdatePasswordVerificationData) => {
    const res = await verifyPassword(data.password);

    if (res.Code === 0) {
      navigate(ROUTES.MY.SETTINGS.SECURITY.PASSWORD.$path());
    }
  };

  if (isPasswordSet === false) {
    return (
      <Navigate
        replace
        to={ROUTES.MY.SETTINGS.SECURITY.PASSWORD.$path()}
      />
    );
  }

  return (
    <PageLayout
      title={t('mine.security')}
      headerConfig={{
        navigateBack: !isDesktop,
      }}>
      <form
        onSubmit={handleSubmit(handleVerifyOtpCode)}
        className='mx-5 mb-safe-offset-4 flex flex-1 flex-col justify-between'>
        <div>
          <label
            htmlFor='phoneNumber'
            className='form-control w-full'>
            <div className='label px-0'>
              <span className='label-text'>{t('security.phone')}</span>
            </div>

            <p className='text-body1 text-secondary'>
              {userInfo?.TelCode} {userInfo?.Phone}
            </p>
          </label>

          <hr className='my-4' />

          <h4 className='mb-2 font-bold'>{t('security.password-not-set')}</h4>

          <label
            htmlFor='password'
            className='form-control w-full'>
            <div className='label px-0'>
              <span className='label-text'>{t('security.old-password')}</span>
            </div>

            <PasswordField
              {...register('password')}
              className='bg-neutral-0'
              placeholder={t('auth.enter-current-password')}
              autoComplete='current-password'
            />

            <FieldMessage
              name='password'
              errors={errors}
            />
          </label>
        </div>

        <ProgressButton
          as='submit'
          isLoading={isPending}
          disabled={!isDirty}
          className='btn btn-primary'
          label='auth.next-step'
        />
      </form>
    </PageLayout>
  );
}

const updatePasswordVerificationSchema = z.object({
  password: z.string(),
});

type UpdatePasswordVerificationData = z.infer<typeof updatePasswordVerificationSchema>;
